import { useEffect, useState } from "react";
import {
  Download,
  Flag,
  GitMerge,
  Plus,
  RotateCcw,
  Search,
  StickyNote,
  Tag as TagIcon,
  Trash2,
  UserPlus,
  X,
} from "lucide-react";
import { ApiError } from "@/lib/api";
import { formatDateTime } from "@/lib/utils";
import { Badge, Button, Dialog, Field, Input, Select } from "@/components/ui/primitives";
import { ConfirmButton } from "@/components/ui/ConfirmButton";
import { useAuth } from "@/features/auth/auth-context";
import { RequirePermission } from "@/features/auth/RequirePermission";
import { useUsers } from "@/features/users/hooks";
import { useCustomers } from "@/features/customers/hooks";
import { SERVICE_CONTRACT_TYPE_LABELS } from "@/features/customers/api";
import { TicketDetailDialog } from "./TicketDetailDialog";
import { NewTicketDialog } from "./NewTicketDialog";
import { useBulkTicketAction, useMergeTickets, useSetCallType, useTags, useTickets } from "./hooks";
import {
  PRIORITY_LABELS,
  STATUS_LABELS,
  priorityStyle,
  statusTone,
} from "./labels";
import { slaBadge } from "./sla";
import type { BulkAction, CallType, TicketListItem, TicketPriority, TicketSortBy, TicketStatus } from "./api";

const CALL_TYPE_LABELS: Record<CallType, string> = {
  amc: "AMC Call",
  one_time: "One Time",
  paid: "Paid Call",
  free: "Free",
};

const PAGE_SIZE_OPTIONS = [10, 25, 50, 100] as const;

const OPEN_STATUSES: TicketStatus[] = [
  "open",
  "assigned",
  "in_progress",
  "waiting_customer",
];

/** Client-side CSV of the given (selected) rows — no round trip needed. */
function exportCsv(rows: TicketListItem[]): void {
  const header = [
    "Ticket #", "Title", "Company", "Priority", "Status", "Engineer",
    "Tags", "Created", "Last updated", "Closed",
  ];
  const esc = (v: unknown) => `"${String(v ?? "").replace(/"/g, '""')}"`;
  const lines = [header.map(esc).join(",")];
  for (const t of rows) {
    lines.push(
      [
        t.ticket_number, t.title, t.customer_name, PRIORITY_LABELS[t.priority],
        STATUS_LABELS[t.status], t.assigned_engineer_name ?? "Unassigned",
        t.tags.join("; "), t.created_at, t.updated_at, t.closed_at ?? "",
      ].map(esc).join(","),
    );
  }
  const blob = new Blob(["﻿" + lines.join("\n")], { type: "text/csv;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `tickets-${new Date().toISOString().slice(0, 10)}.csv`;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 5000);
}

export default function TicketsPage({
  initialDetailId,
  initialPreset,
}: {
  initialDetailId?: string | null;
  // A one-shot filter handed over from a dashboard KPI card (with a nonce so a
  // repeat click re-applies it).
  initialPreset?: { preset: string; nonce: number } | null;
}) {
  const { can, user } = useAuth();
  // Call-type billing classification is reserved to administrators — only they
  // see the column and can change it.
  const isAdmin = user?.role_code === "admin";
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [priorityFilter, setPriorityFilter] = useState("");
  const [myTickets, setMyTickets] = useState(false);
  const [overdueFilter, setOverdueFilter] = useState(false);

  // Apply a preset that arrived from the dashboard (open / overdue / resolved).
  useEffect(() => {
    if (!initialPreset) return;
    const p = initialPreset.preset;
    setStatusFilter(p === "open" ? "open_only" : p === "resolved" ? "resolved" : "");
    setOverdueFilter(p === "overdue");
    setPage(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialPreset?.nonce]);
  // Advanced filters + sort.
  const [customerFilter, setCustomerFilter] = useState("");
  const [assigneeFilter, setAssigneeFilter] = useState("");
  const [serviceTypeFilter, setServiceTypeFilter] = useState("");
  const [createdFrom, setCreatedFrom] = useState("");
  const [createdTo, setCreatedTo] = useState("");
  const [tagFilter, setTagFilter] = useState("");
  const [sortByDir, setSortByDir] = useState("created_at:desc");
  const [detailId, setDetailId] = useState<string | null>(initialDetailId ?? null);
  const [formOpen, setFormOpen] = useState(false);

  // Bulk selection (current page).
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [bulkMode, setBulkMode] = useState<
    null | "assign" | "note" | "priority" | "tags" | "merge" | "reopen"
  >(null);
  const [mergeTarget, setMergeTarget] = useState("");
  const mergeM = useMergeTickets();
  const [bulkAssignee, setBulkAssignee] = useState("");
  const [bulkNote, setBulkNote] = useState("");
  const [bulkPriority, setBulkPriority] = useState("");
  const [bulkTagsText, setBulkTagsText] = useState("");
  const [bulkTagsMode, setBulkTagsMode] = useState<"add" | "remove">("add");
  const [bulkResult, setBulkResult] = useState<{
    updated: number;
    failed: number;
    errors: { ticket_id: string; error: string }[];
  } | null>(null);
  const [bulkReopenReason, setBulkReopenReason] = useState("");
  const [bulkError, setBulkError] = useState<string | null>(null);
  const bulk = useBulkTicketAction();

  // Explicit assignee dropdown wins; otherwise the "My tickets" toggle applies.
  const assigneeParam = assigneeFilter || (myTickets && user ? user.id : "");
  const [sortBy, sortDir] = sortByDir.split(":") as [TicketSortBy, "asc" | "desc"];

  const filters = {
    page,
    page_size: pageSize,
    search: search.trim(),
    sort_by: sortBy,
    sort_dir: sortDir,
    ...(assigneeParam === "unassigned"
      ? { unassigned: true }
      : assigneeParam
        ? { assigned_engineer_id: assigneeParam }
        : {}),
    ...(customerFilter ? { customer_id: customerFilter } : {}),
    ...(serviceTypeFilter ? { service_type: serviceTypeFilter } : {}),
    ...(tagFilter ? { tags: [tagFilter] } : {}),
    ...(overdueFilter ? { overdue: true } : {}),
    ...(createdFrom ? { created_from: createdFrom } : {}),
    ...(createdTo ? { created_to: createdTo } : {}),
    ...(statusFilter === "open_only"
      ? { status: OPEN_STATUSES }
      : statusFilter
        ? { status: [statusFilter as TicketStatus] }
        : {}),
    ...(priorityFilter ? { priority: [priorityFilter as TicketPriority] } : {}),
  };

  const { data, isLoading, isError, error } = useTickets(filters);
  const rows = data?.items ?? [];

  const canAssign = can("ticket:assign") && can("user:read");
  const canBulk = can("ticket:update") && !user?.customer_id;
  const { data: staffPage } = useUsers({ page_size: 100 }, { enabled: canAssign });
  const engineers = (staffPage?.items ?? []).filter(
    (u) => u.role_code !== "customer" && u.is_active,
  );
  // For the customer filter dropdown (staff only).
  const canReadCustomers = can("customer:read") && !user?.customer_id;
  const { data: customerPage } = useCustomers({ page_size: 100 }, { enabled: canReadCustomers });
  const customerOptions = customerPage?.items ?? [];
  const { data: allTags } = useTags();

  const totalPages = data ? Math.max(1, Math.ceil(data.total / pageSize)) : 1;
  const selectedRows = rows.filter((t) => selected.has(t.id));
  const allOnPageSelected = rows.length > 0 && rows.every((t) => selected.has(t.id));
  // Base columns (bulk checkbox adds one); the Call type column shows only for
  // administrators, so it is counted only then.
  const colCount = (canBulk ? 10 : 9) + (isAdmin ? 1 : 0);
  const canSetCallType = isAdmin;

  function toggle(id: string) {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }
  function toggleAll() {
    setSelected((prev) => {
      const next = new Set(prev);
      if (allOnPageSelected) rows.forEach((t) => next.delete(t.id));
      else rows.forEach((t) => next.add(t.id));
      return next;
    });
  }
  function clearSelection() {
    setSelected(new Set());
    setBulkResult(null);
    setBulkError(null);
  }

  async function runMerge() {
    if (!mergeTarget) return;
    setBulkError(null);
    setBulkResult(null);
    const sources = [...selected].filter((id) => id !== mergeTarget);
    try {
      await mergeM.mutateAsync({ target_id: mergeTarget, source_ids: sources });
      setBulkResult({ updated: sources.length, failed: 0, errors: [] });
      setBulkMode(null);
      setMergeTarget("");
      setSelected(new Set());
    } catch (err) {
      setBulkError(err instanceof ApiError ? err.message : "The merge failed.");
    }
  }

  async function runBulk(
    action: BulkAction,
    extra: {
      assignee_id?: string;
      note?: string;
      priority?: TicketPriority;
      tags?: string[];
      reason?: string;
    } = {},
  ) {
    setBulkError(null);
    setBulkResult(null);
    try {
      const res = await bulk.mutateAsync({
        ticket_ids: [...selected],
        action,
        assignee_id: extra.assignee_id ?? null,
        note: extra.note ?? null,
        priority: extra.priority ?? null,
        tags: extra.tags ?? null,
        reason: extra.reason ?? null,
      });
      setBulkResult({ updated: res.updated, failed: res.failed, errors: res.errors });
      setBulkMode(null);
      setBulkNote("");
      setBulkAssignee("");
      setBulkPriority("");
      setBulkTagsText("");
      setBulkReopenReason("");
      // Keep only the rows that failed selected, so the user can retry/inspect.
      const failedIds = new Set(res.errors.map((e) => e.ticket_id));
      setSelected((prev) => new Set([...prev].filter((id) => failedIds.has(id))));
    } catch (err) {
      setBulkError(err instanceof ApiError ? err.message : "The bulk action failed.");
    }
  }

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">Tickets</h1>
          <p className="mt-1 text-sm text-muted-foreground">
            {user?.customer_id
              ? "Your support requests."
              : "Support requests across all customers."}
          </p>
        </div>
        <RequirePermission permission="ticket:create">
          <Button onClick={() => setFormOpen(true)}>
            <Plus className="h-4 w-4" aria-hidden="true" />
            New ticket
          </Button>
        </RequirePermission>
      </div>

      <div className="flex flex-wrap gap-2">
        <div className="relative min-w-[220px] flex-1">
          <Search
            className="pointer-events-none absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground"
            aria-hidden="true"
          />
          <Input
            aria-label="Search tickets"
            placeholder="Search number, title or description"
            className="pl-8"
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              setPage(1);
            }}
          />
        </div>
        {!user?.customer_id && (
          <Button
            variant={myTickets ? "default" : "outline"}
            onClick={() => {
              setMyTickets((v) => !v);
              setPage(1);
            }}
          >
            My tickets
          </Button>
        )}
        {!user?.customer_id && (
          <Button
            variant={overdueFilter ? "default" : "outline"}
            onClick={() => {
              setOverdueFilter((v) => !v);
              setPage(1);
            }}
          >
            Overdue
          </Button>
        )}
        <Select
          aria-label="Filter by status"
          className="w-auto min-w-[170px]"
          value={statusFilter}
          onChange={(e) => {
            setStatusFilter(e.target.value);
            setPage(1);
          }}
        >
          <option value="">All statuses</option>
          <option value="open_only">Open (not closed)</option>
          {Object.entries(STATUS_LABELS).map(([value, label]) => (
            <option key={value} value={value}>
              {label}
            </option>
          ))}
        </Select>
        <Select
          aria-label="Filter by priority"
          className="w-auto min-w-[150px]"
          value={priorityFilter}
          onChange={(e) => {
            setPriorityFilter(e.target.value);
            setPage(1);
          }}
        >
          <option value="">All priorities</option>
          {Object.entries(PRIORITY_LABELS).map(([value, label]) => (
            <option key={value} value={value}>
              {label}
            </option>
          ))}
        </Select>
      </div>

      {/* Advanced filters + sort (staff only). */}
      {!user?.customer_id && (
        <div className="flex flex-wrap items-center gap-2">
          <Select
            aria-label="Sort by"
            className="w-auto min-w-[180px]"
            value={sortByDir}
            onChange={(e) => {
              setSortByDir(e.target.value);
              setPage(1);
            }}
          >
            <option value="created_at:desc">Sort: Newest first</option>
            <option value="created_at:asc">Sort: Oldest first</option>
            <option value="updated_at:desc">Sort: Recently updated</option>
            <option value="priority:desc">Sort: Priority (high → low)</option>
            <option value="sla:asc">Sort: SLA (soonest due)</option>
          </Select>
          <Select
            aria-label="Filter by customer"
            className="w-auto min-w-[170px]"
            value={customerFilter}
            onChange={(e) => {
              setCustomerFilter(e.target.value);
              setPage(1);
            }}
          >
            <option value="">All customers</option>
            {customerOptions.map((c) => (
              <option key={c.id} value={c.id}>
                {c.company_name}
              </option>
            ))}
          </Select>
          {canAssign && (
            <Select
              aria-label="Filter by assignee"
              className="w-auto min-w-[160px]"
              value={assigneeFilter}
              onChange={(e) => {
                setAssigneeFilter(e.target.value);
                setPage(1);
              }}
            >
              <option value="">Any assignee</option>
              <option value="unassigned">Unassigned</option>
              {engineers.map((e) => (
                <option key={e.id} value={e.id}>
                  {e.full_name}
                </option>
              ))}
            </Select>
          )}
          <Select
            aria-label="Filter by service type"
            className="w-auto min-w-[170px]"
            value={serviceTypeFilter}
            onChange={(e) => {
              setServiceTypeFilter(e.target.value);
              setPage(1);
            }}
          >
            <option value="">Any service type</option>
            {Object.entries(SERVICE_CONTRACT_TYPE_LABELS).map(([v, label]) => (
              <option key={v} value={v}>
                {label}
              </option>
            ))}
          </Select>
          {(allTags ?? []).length > 0 && (
            <Select
              aria-label="Filter by tag"
              className="w-auto min-w-[150px]"
              value={tagFilter}
              onChange={(e) => {
                setTagFilter(e.target.value);
                setPage(1);
              }}
            >
              <option value="">Any tag</option>
              {(allTags ?? []).map((t) => (
                <option key={t.name} value={t.name}>
                  {t.name} ({t.count})
                </option>
              ))}
            </Select>
          )}
          <Input
            type="date"
            aria-label="Created from"
            className="w-auto"
            title="Created from"
            value={createdFrom}
            onChange={(e) => {
              setCreatedFrom(e.target.value);
              setPage(1);
            }}
          />
          <Input
            type="date"
            aria-label="Created to"
            className="w-auto"
            title="Created to"
            value={createdTo}
            onChange={(e) => {
              setCreatedTo(e.target.value);
              setPage(1);
            }}
          />
        </div>
      )}

      {/* Bulk action bar — appears once tickets are selected. */}
      {canBulk && selected.size > 0 && (
        <div className="flex flex-wrap items-center gap-2 rounded-lg border border-primary/40 bg-primary/5 px-3 py-2 text-sm">
          <span className="font-medium">{selected.size} selected</span>
          <div className="ml-auto flex flex-wrap gap-2">
            {canAssign && (
              <Button variant="outline" onClick={() => setBulkMode("assign")}>
                <UserPlus className="h-4 w-4" aria-hidden="true" /> Assign
              </Button>
            )}
            <Button variant="outline" onClick={() => setBulkMode("priority")}>
              <Flag className="h-4 w-4" aria-hidden="true" /> Priority
            </Button>
            <Button variant="outline" onClick={() => setBulkMode("reopen")}>
              <RotateCcw className="h-4 w-4" aria-hidden="true" /> Reopen
            </Button>
            {can("ticket:note:internal") && (
              <Button variant="outline" onClick={() => setBulkMode("note")}>
                <StickyNote className="h-4 w-4" aria-hidden="true" /> Internal note
              </Button>
            )}
            <Button variant="outline" onClick={() => setBulkMode("tags")}>
              <TagIcon className="h-4 w-4" aria-hidden="true" /> Tags
            </Button>
            {selected.size >= 2 && (
              <Button
                variant="outline"
                onClick={() => {
                  setMergeTarget("");
                  setBulkMode("merge");
                }}
              >
                <GitMerge className="h-4 w-4" aria-hidden="true" /> Merge
              </Button>
            )}
            <Button variant="outline" onClick={() => exportCsv(selectedRows)}>
              <Download className="h-4 w-4" aria-hidden="true" /> Export
            </Button>
            {/* Deleting tickets is destructive and administrator-only. */}
            {user?.role_code === "admin" && (
              <ConfirmButton
                buttonVariant="outline"
                title="Delete tickets?"
                confirmLabel="Delete tickets"
                message={
                  <>
                    Delete <span className="font-medium text-foreground">{selected.size}</span>{" "}
                    ticket{selected.size === 1 ? "" : "s"}? They’ll be removed from every view.
                    This can’t be undone from the app.
                  </>
                }
                onConfirm={() => runBulk("delete")}
              >
                <Trash2 className="h-4 w-4 text-destructive" aria-hidden="true" /> Delete
              </ConfirmButton>
            )}
            <Button variant="ghost" onClick={clearSelection}>
              <X className="h-4 w-4" aria-hidden="true" /> Clear
            </Button>
          </div>
        </div>
      )}

      {bulkResult && (
        <div className="space-y-2 rounded-md border border-border bg-muted/40 px-3 py-2 text-sm">
          <div>
            Bulk action applied to <strong>{bulkResult.updated}</strong> ticket
            {bulkResult.updated === 1 ? "" : "s"}
            {bulkResult.failed > 0 && (
              <> · <strong>{bulkResult.failed}</strong> could not be updated (still selected).</>
            )}
          </div>
          {/* Show the distinct reasons so a failure is legible, not a mystery. */}
          {bulkResult.errors.length > 0 && (
            <ul className="list-disc space-y-0.5 pl-5 text-xs text-muted-foreground">
              {[...new Set(bulkResult.errors.map((e) => e.error))].map((msg) => (
                <li key={msg}>{msg}</li>
              ))}
            </ul>
          )}
        </div>
      )}
      {bulkError && (
        <div role="alert" className="rounded-md border border-destructive/40 bg-destructive/10 px-3 py-2 text-sm text-destructive">
          {bulkError}
        </div>
      )}

      <div className="overflow-x-auto rounded-lg border border-border">
        <table className="w-full min-w-[1040px] text-sm">
          <thead className="bg-muted/50 text-left text-xs uppercase text-muted-foreground">
            <tr>
              {canBulk && (
                <th className="px-3 py-2.5">
                  <input
                    type="checkbox"
                    aria-label="Select all on this page"
                    checked={allOnPageSelected}
                    onChange={toggleAll}
                  />
                </th>
              )}
              <th className="px-4 py-2.5 font-medium">Ticket</th>
              <th className="px-4 py-2.5 font-medium">Company</th>
              <th className="px-4 py-2.5 font-medium">Priority</th>
              <th className="px-4 py-2.5 font-medium">Status</th>
              <th className="px-4 py-2.5 font-medium">Resolution SLA</th>
              <th className="px-4 py-2.5 font-medium">Engineer</th>
              <th className="px-4 py-2.5 font-medium">Raised</th>
              <th className="px-4 py-2.5 font-medium">Last updated</th>
              <th className="px-4 py-2.5 font-medium">Closed</th>
              {isAdmin && <th className="px-4 py-2.5 font-medium">Call type</th>}
            </tr>
          </thead>
          <tbody>
            {isLoading && (
              <tr>
                <td colSpan={colCount} className="px-4 py-8 text-center text-muted-foreground">
                  Loading…
                </td>
              </tr>
            )}
            {isError && (
              <tr>
                <td colSpan={colCount} className="px-4 py-8 text-center text-destructive">
                  {error instanceof ApiError ? error.message : "Could not load tickets."}
                </td>
              </tr>
            )}
            {data?.items.length === 0 && (
              <tr>
                <td colSpan={colCount} className="px-4 py-8 text-center text-muted-foreground">
                  No tickets match these filters.
                </td>
              </tr>
            )}
            {rows.map((t) => (
              <tr key={t.id} className="border-t border-border" data-selected={selected.has(t.id)}>
                {canBulk && (
                  <td className="px-3 py-3">
                    <input
                      type="checkbox"
                      aria-label={`Select ${t.ticket_number}`}
                      checked={selected.has(t.id)}
                      onChange={() => toggle(t.id)}
                    />
                  </td>
                )}
                <td className="px-4 py-3">
                  <button
                    type="button"
                    onClick={() => setDetailId(t.id)}
                    className="text-left font-semibold text-primary hover:underline"
                  >
                    {t.ticket_number}
                  </button>
                  <div className="text-xs text-muted-foreground">{t.title}</div>
                  {t.tags.length > 0 && (
                    <div className="mt-1 flex flex-wrap gap-1">
                      {t.tags.map((tag) => (
                        <span
                          key={tag}
                          className="rounded bg-accent px-1.5 py-0.5 text-[10px] font-medium text-accent-foreground"
                        >
                          {tag}
                        </span>
                      ))}
                    </div>
                  )}
                </td>
                <td className="px-4 py-3 text-muted-foreground">{t.customer_name}</td>
                <td className="px-4 py-3">
                  <span
                    className="rounded-full px-2 py-0.5 text-xs font-medium text-white"
                    style={priorityStyle(t.priority)}
                  >
                    {PRIORITY_LABELS[t.priority]}
                  </span>
                </td>
                <td className="px-4 py-3">
                  <div className="flex flex-wrap items-center gap-1">
                    <Badge tone={statusTone(t.status)}>{STATUS_LABELS[t.status]}</Badge>
                    {t.reopened_count > 0 && (
                      <Badge tone="warning">Reopened{t.reopened_count > 1 ? ` ×${t.reopened_count}` : ""}</Badge>
                    )}
                  </div>
                </td>
                <td className="px-4 py-3">
                  {(() => {
                    const sla = slaBadge(t.sla.resolution_state, t.sla.resolution_due_at);
                    return <Badge tone={sla.tone}>{sla.label}</Badge>;
                  })()}
                </td>
                <td className="px-4 py-3 text-muted-foreground">
                  {t.assigned_engineer_name ?? "Unassigned"}
                </td>
                <td className="px-4 py-3 text-muted-foreground">
                  {formatDateTime(t.created_at)}
                </td>
                <td className="px-4 py-3 text-muted-foreground">
                  {formatDateTime(t.updated_at)}
                </td>
                <td className="px-4 py-3 text-muted-foreground">
                  {t.closed_at ? formatDateTime(t.closed_at) : "—"}
                </td>
                {isAdmin && (
                  <td className="px-4 py-3">
                    <CallTypeCell ticket={t} canEdit={canSetCallType} />
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="flex flex-wrap items-center justify-between gap-2 text-sm text-muted-foreground">
        <div className="flex items-center gap-2">
          <span>{data ? `${data.total} ticket${data.total === 1 ? "" : "s"}` : ""}</span>
          <Select
            aria-label="Tickets per page"
            className="h-8 w-auto min-w-[120px]"
            value={String(pageSize)}
            onChange={(e) => {
              setPageSize(Number(e.target.value));
              setPage(1); // page count changes, so jump back to the first page
            }}
          >
            {PAGE_SIZE_OPTIONS.map((n) => (
              <option key={n} value={n}>
                {n} per page
              </option>
            ))}
          </Select>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" disabled={page <= 1} onClick={() => setPage((p) => p - 1)}>
            Previous
          </Button>
          <span>
            Page {page} of {totalPages}
          </span>
          <Button
            variant="outline"
            disabled={page >= totalPages}
            onClick={() => setPage((p) => p + 1)}
          >
            Next
          </Button>
        </div>
      </div>

      {/* Bulk assign dialog */}
      <Dialog open={bulkMode === "assign"} onClose={() => setBulkMode(null)} title="Assign selected tickets">
        <div className="space-y-4">
          <p className="text-sm text-muted-foreground">
            Assign the {selected.size} selected ticket{selected.size === 1 ? "" : "s"} to a staff member.
          </p>
          <Field label="Assign to" htmlFor="bulk_assignee">
            <Select
              id="bulk_assignee"
              value={bulkAssignee}
              onChange={(e) => setBulkAssignee(e.target.value)}
            >
              <option value="">Select a staff member…</option>
              {engineers.map((e) => (
                <option key={e.id} value={e.id}>
                  {e.full_name}
                </option>
              ))}
            </Select>
          </Field>
          <div className="flex justify-end gap-2">
            <Button variant="ghost" onClick={() => setBulkMode(null)}>Cancel</Button>
            <Button
              disabled={!bulkAssignee || bulk.isPending}
              onClick={() => void runBulk("assign", { assignee_id: bulkAssignee })}
            >
              {bulk.isPending ? "Assigning…" : "Assign"}
            </Button>
          </div>
        </div>
      </Dialog>

      {/* Merge dialog — pick which selected ticket is the target. */}
      <Dialog open={bulkMode === "merge"} onClose={() => setBulkMode(null)} title="Merge tickets">
        <div className="space-y-4">
          <p className="text-sm text-muted-foreground">
            Choose the ticket to <strong>keep</strong>. The others are merged into it —
            their notes, attachments and tags move over, and they're closed with a
            reference. Only tickets for the same customer can be merged.
          </p>
          <div className="max-h-64 space-y-1 overflow-y-auto rounded-md border border-border p-2">
            {selectedRows.map((t) => (
              <label
                key={t.id}
                className="flex cursor-pointer items-start gap-2 rounded p-2 text-sm hover:bg-accent"
              >
                <input
                  type="radio"
                  name="merge_target"
                  className="mt-0.5"
                  checked={mergeTarget === t.id}
                  onChange={() => setMergeTarget(t.id)}
                />
                <span className="min-w-0">
                  <span className="block truncate font-semibold">
                    {t.ticket_number}
                  </span>
                  <span className="block truncate text-xs text-muted-foreground">
                    {t.title} · {t.customer_name}
                  </span>
                </span>
              </label>
            ))}
          </div>
          <div className="flex justify-end gap-2">
            <Button variant="ghost" onClick={() => setBulkMode(null)}>Cancel</Button>
            <Button disabled={!mergeTarget || mergeM.isPending} onClick={() => void runMerge()}>
              {mergeM.isPending
                ? "Merging…"
                : `Merge ${Math.max(0, selected.size - 1)} into this ticket`}
            </Button>
          </div>
        </div>
      </Dialog>

      {/* Bulk reopen dialog — reopening requires a reason. */}
      <Dialog open={bulkMode === "reopen"} onClose={() => setBulkMode(null)} title="Reopen tickets">
        <div className="space-y-4">
          <p className="text-sm text-muted-foreground">
            Reopen the {selected.size} selected ticket{selected.size === 1 ? "" : "s"}. Only
            closed, resolved or cancelled tickets can be reopened — others are skipped.
          </p>
          <Field label="Reason (required)" htmlFor="bulk_reopen_reason">
            <Input
              id="bulk_reopen_reason"
              value={bulkReopenReason}
              onChange={(e) => setBulkReopenReason(e.target.value)}
            />
          </Field>
          <div className="flex justify-end gap-2">
            <Button variant="ghost" onClick={() => setBulkMode(null)}>Cancel</Button>
            <Button
              disabled={!bulkReopenReason.trim() || bulk.isPending}
              onClick={() => void runBulk("reopen", { reason: bulkReopenReason.trim() })}
            >
              {bulk.isPending ? "Reopening…" : "Reopen"}
            </Button>
          </div>
        </div>
      </Dialog>

      {/* Bulk change-priority dialog */}
      <Dialog open={bulkMode === "priority"} onClose={() => setBulkMode(null)} title="Change priority">
        <div className="space-y-4">
          <p className="text-sm text-muted-foreground">
            Set the priority of the {selected.size} selected ticket
            {selected.size === 1 ? "" : "s"}. SLA deadlines are recalculated. Closed or
            cancelled tickets are skipped.
          </p>
          <Field label="Priority" htmlFor="bulk_priority">
            <Select
              id="bulk_priority"
              value={bulkPriority}
              onChange={(e) => setBulkPriority(e.target.value)}
            >
              <option value="">Select a priority…</option>
              {Object.entries(PRIORITY_LABELS).map(([value, label]) => (
                <option key={value} value={value}>
                  {label}
                </option>
              ))}
            </Select>
          </Field>
          <div className="flex justify-end gap-2">
            <Button variant="ghost" onClick={() => setBulkMode(null)}>Cancel</Button>
            <Button
              disabled={!bulkPriority || bulk.isPending}
              onClick={() =>
                void runBulk("change_priority", { priority: bulkPriority as TicketPriority })
              }
            >
              {bulk.isPending ? "Updating…" : "Change priority"}
            </Button>
          </div>
        </div>
      </Dialog>

      {/* Bulk add/remove tags dialog */}
      <Dialog open={bulkMode === "tags"} onClose={() => setBulkMode(null)} title="Add or remove tags">
        <div className="space-y-4">
          <div className="flex gap-2">
            <Button
              variant={bulkTagsMode === "add" ? "default" : "outline"}
              onClick={() => setBulkTagsMode("add")}
            >
              Add
            </Button>
            <Button
              variant={bulkTagsMode === "remove" ? "default" : "outline"}
              onClick={() => setBulkTagsMode("remove")}
            >
              Remove
            </Button>
          </div>
          <Field label="Tags (comma-separated)" htmlFor="bulk_tags">
            <Input
              id="bulk_tags"
              placeholder="e.g. urgent, vip, hardware"
              value={bulkTagsText}
              onChange={(e) => setBulkTagsText(e.target.value)}
            />
          </Field>
          <p className="text-xs text-muted-foreground">
            {bulkTagsMode === "add" ? "Adds" : "Removes"} these tags on the {selected.size}{" "}
            selected ticket{selected.size === 1 ? "" : "s"}.
          </p>
          <div className="flex justify-end gap-2">
            <Button variant="ghost" onClick={() => setBulkMode(null)}>Cancel</Button>
            <Button
              disabled={!bulkTagsText.trim() || bulk.isPending}
              onClick={() => {
                const tags = bulkTagsText
                  .split(",")
                  .map((t) => t.trim())
                  .filter(Boolean);
                if (tags.length === 0) return;
                void runBulk(bulkTagsMode === "add" ? "add_tags" : "remove_tags", { tags });
              }}
            >
              {bulk.isPending ? "Applying…" : bulkTagsMode === "add" ? "Add tags" : "Remove tags"}
            </Button>
          </div>
        </div>
      </Dialog>

      {/* Bulk internal note dialog */}
      <Dialog open={bulkMode === "note"} onClose={() => setBulkMode(null)} title="Add internal note">
        <div className="space-y-4">
          <p className="text-sm text-muted-foreground">
            Adds an internal (staff-only) note to the {selected.size} selected ticket
            {selected.size === 1 ? "" : "s"}.
          </p>
          <Field label="Note" htmlFor="bulk_note">
            <textarea
              id="bulk_note"
              rows={4}
              className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm outline-none focus-visible:ring-2 focus-visible:ring-ring"
              value={bulkNote}
              onChange={(e) => setBulkNote(e.target.value)}
            />
          </Field>
          <div className="flex justify-end gap-2">
            <Button variant="ghost" onClick={() => setBulkMode(null)}>Cancel</Button>
            <Button
              disabled={!bulkNote.trim() || bulk.isPending}
              onClick={() => void runBulk("add_note", { note: bulkNote.trim() })}
            >
              {bulk.isPending ? "Adding…" : "Add note"}
            </Button>
          </div>
        </div>
      </Dialog>

      <NewTicketDialog open={formOpen} onClose={() => setFormOpen(false)} />
      <TicketDetailDialog
        ticketId={detailId}
        onClose={() => setDetailId(null)}
        engineers={engineers}
      />
    </div>
  );
}

function CallTypeCell({ ticket, canEdit }: { ticket: TicketListItem; canEdit: boolean }) {
  const setCall = useSetCallType();
  // The type being edited in the dialog (paid/one_time/free need details).
  const [editing, setEditing] = useState<CallType | null>(null);
  const [amount, setAmount] = useState("");
  const [remark, setRemark] = useState("");
  const [err, setErr] = useState<string | null>(null);

  const current = ticket.call_type;
  const label =
    current === null
      ? "—"
      : CALL_TYPE_LABELS[current] + (ticket.call_amount ? ` · ₹${ticket.call_amount}` : "");

  if (!canEdit) {
    return (
      <span className="text-xs text-muted-foreground" title={ticket.call_remark ?? undefined}>
        {label}
      </span>
    );
  }

  const needsAmount = editing === "one_time" || editing === "paid";

  async function choose(value: string) {
    setErr(null);
    if (!value) return;
    const ct = value as CallType;
    // AMC saves immediately; the other three collect an amount and/or remark.
    if (ct === "amc") {
      try {
        await setCall.mutateAsync({ id: ticket.id, call_type: ct });
      } catch (e) {
        setErr(e instanceof ApiError ? e.message : "Could not update.");
      }
      return;
    }
    setAmount(ticket.call_amount ?? "");
    setRemark(ticket.call_remark ?? "");
    setEditing(ct);
  }

  async function submit() {
    if (!editing) return;
    setErr(null);
    try {
      await setCall.mutateAsync({
        id: ticket.id,
        call_type: editing,
        ...(needsAmount ? { amount } : {}),
        remark,
      });
      setEditing(null);
      setAmount("");
      setRemark("");
    } catch (e) {
      setErr(e instanceof ApiError ? e.message : "Could not update.");
    }
  }

  return (
    <>
      <Select
        aria-label={`Call type for ${ticket.ticket_number}`}
        className="h-8 w-auto min-w-[130px]"
        value={current ?? ""}
        onChange={(e) => void choose(e.target.value)}
      >
        <option value="">Set call type…</option>
        <option value="amc">AMC Call</option>
        <option value="one_time">One Time</option>
        <option value="paid">Paid Call</option>
        <option value="free">Free</option>
      </Select>
      {ticket.call_amount && (
        <div className="mt-0.5 text-[11px] tabular-nums text-muted-foreground">
          ₹{ticket.call_amount}
        </div>
      )}
      <Dialog
        open={editing !== null}
        onClose={() => setEditing(null)}
        title={editing ? `${CALL_TYPE_LABELS[editing]} — details` : "Call details"}
      >
        <div className="space-y-3">
          <p className="text-sm text-muted-foreground">
            {needsAmount
              ? "Enter the chargeable amount and an optional remark for this call."
              : "Add an optional remark / narration for this call."}
          </p>
          {err && (
            <div role="alert" className="rounded-md bg-destructive/10 px-3 py-1.5 text-xs text-destructive">
              {err}
            </div>
          )}
          {needsAmount && (
            <Field label="Amount (₹)" htmlFor={`amt-${ticket.id}`}>
              <Input
                id={`amt-${ticket.id}`}
                type="number"
                min="0"
                step="0.01"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                autoFocus
              />
            </Field>
          )}
          <Field label="Remark / narration (optional)" htmlFor={`rmk-${ticket.id}`}>
            <Input
              id={`rmk-${ticket.id}`}
              type="text"
              maxLength={500}
              placeholder="e.g. Toner replaced, on-site visit"
              value={remark}
              onChange={(e) => setRemark(e.target.value)}
              autoFocus={!needsAmount}
            />
          </Field>
          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={() => setEditing(null)}>
              Cancel
            </Button>
            <Button
              onClick={() => void submit()}
              disabled={(needsAmount && !amount) || setCall.isPending}
            >
              Save
            </Button>
          </div>
        </div>
      </Dialog>
    </>
  );
}
